import { StrictMode, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from '@utils/i18n';
import { PersistGate } from 'redux-persist/integration/react';
import { AppBarLoader } from '@components/AppBar/app-bar-loader';
import { MsalProvider } from '@azure/msal-react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { CircularProgress } from '@mui/material';
import { PARTICIPANT_TOKEN_PARAM } from '@constants';
import { Nullable } from '@customTypes';

import App from './App';
import { persistor, store } from './store/store';
import './index.css';

const appBarLoader = new AppBarLoader();

const AppWrapper = () => {
  const [msalInstance, setMsalInstance] = useState<Nullable<IPublicClientApplication>>(null);
  const hasParticipantToken = new URLSearchParams(window.location.search).has(PARTICIPANT_TOKEN_PARAM);

  useEffect(() => {
    const loadMsalInstance = async () => {
      const instance = await appBarLoader.getMsalInstance();
      if (instance) {
        setMsalInstance(instance);
      }
    };

    void loadMsalInstance();
  }, []);

  if (!msalInstance && !hasParticipantToken) {
    return <CircularProgress />;
  }

  return (
    <StrictMode>
      <Provider store={store}>
        <BrowserRouter>
          <PersistGate loading={null} persistor={persistor}>
            <I18nextProvider i18n={i18n}>
              {hasParticipantToken ? (
                <App instance={msalInstance} />
              ) : (
                <MsalProvider instance={msalInstance!}>
                  <App instance={msalInstance} />
                </MsalProvider>
              )}
            </I18nextProvider>
          </PersistGate>
        </BrowserRouter>
      </Provider>
    </StrictMode>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(<AppWrapper />);
